import React, { useMemo, useState } from 'react';
import { Filter, SlidersHorizontal } from 'lucide-react';
import { formatEuro } from '../utils/templates';
import { PageLayout, PageHeader, PageCard, pageStyles } from './PageLayout';

// Dark-Mode-Erkennung wie in anderen Komponenten
const useIsDark = () => typeof window !== 'undefined' && document.documentElement.classList.contains('dark');

interface KundenKarteiProps {
  customers: any[];
  allSparten: unknown[];
  currentUser: any;
  allUsers: any[];
  onSelectCustomer?: (customer: any) => void;
}

// Gleiche Zuordnung wie in Bestands-/Dashboard-Views: welche Kunden gehören zum Mitarbeiter?
const checkMatch = (allowed: any, c: any) => {
  const normalizeNumber = (v: any) => v ? String(v).replace(/[^0-9]/g, '').replace(/^0+/, '') : '';
  const normalizeName = (n: any) => n ? n.replace(/[:.;,]/g, ' ').replace(/\s+/g, ' ').trim() : '';

  if (!allowed || !Array.isArray(allowed)) return false;
  if (allowed.some((u: any) => u.role === 'admin')) return true;
  return allowed.some((u: any) => {
    if (!u) return false;
    const uAdv = normalizeNumber(u.adv), cAdp = normalizeNumber(c.adp), cBtnr = normalizeNumber(c.btnr), uBtnr = normalizeNumber(u.btnr);
    if (uAdv.length > 2 && ((cAdp && uAdv.includes(cAdp)) || (cBtnr && uAdv.includes(cBtnr)))) return true;
    if (uBtnr.length > 2 && ((cAdp && uBtnr.includes(cAdp)) || (cBtnr && uBtnr.includes(cBtnr)))) return true;
    const uName = normalizeName(u.name).toLowerCase(), cAdpName = normalizeName(c.adp).toLowerCase();
    return uName.length > 2 && cAdpName.includes(uName);
  });
};

// Geldwerte aus Verträgen robust parsen (ähnlich wie im Dashboard)
const parseMoney = (val: any): number => {
  if (val == null) return 0;
  let str = String(val).replace(/[€$EUR\s]/gi, '').trim();
  if (!str) return 0;
  if (str.includes('.') && str.includes(',')) {
    if (str.lastIndexOf(',') > str.lastIndexOf('.')) {
      str = str.replace(/\./g, '').replace(',', '.');
    } else {
      str = str.replace(/,/g, '');
    }
  } else if (str.includes(',')) {
    str = str.replace(',', '.');
  } else if (str.includes('.')) {
    if (/\.\d{3}$/.test(str)) str = str.replace(/\./g, '');
  }
  const num = parseFloat(str);
  return isNaN(num) ? 0 : num;
};

const KundenKartei: React.FC<KundenKarteiProps> = ({ customers, allSparten, currentUser, allUsers, onSelectCustomer }) => {
  const isDark = useIsDark();

  // Nur eigene Kunden (bzw. alle bei Admin)
  const myCustomers = useMemo(() => {
    if (!currentUser) return [];
    if (currentUser.role === 'admin') return customers;
    const allowedUserIds = [currentUser.id, ...(currentUser.allowedToView || [])];
    return customers.filter((c: any) => checkMatch(allUsers.filter((u: any) => allowedUserIds.includes(u.id)), c));
  }, [customers, currentUser, allUsers]);

  const spartenList = useMemo(
    () => (allSparten || []).map(s => String(s)).filter(Boolean).sort((a, b) => a.localeCompare(b)),
    [allSparten]
  );

  // Filter-States
  const [selectedSparte, setSelectedSparte] = useState<string>('');
  const [minContracts, setMinContracts] = useState<string>('');
  const [maxContracts, setMaxContracts] = useState<string>('');
  const [minBeitrag, setMinBeitrag] = useState<string>('');
  const [maxBeitrag, setMaxBeitrag] = useState<string>('');
  const [nameSearch, setNameSearch] = useState<string>('');

  // Hilfsfunktion: Aggregierte Kennzahlen je Kunde abhängig von Sparten-Filter
  const withStats = useMemo(() => {
    return myCustomers.map((c: any) => {
      const vertraege = c.verträge ?? c.vertraege ?? [];
      const filteredVertraege = selectedSparte
        ? vertraege.filter((v: any) => String(v.sparte || '').trim() === selectedSparte)
        : vertraege;
      const contractCount = filteredVertraege.length;
      let totalBeitrag = 0;
      filteredVertraege.forEach((v: any) => {
        const jab = parseMoney(v.JAB ?? v.jab ?? v.beitrag ?? v.Beitrag ?? v.jahresbeitrag ?? v.Jahresbeitrag);
        totalBeitrag += jab;
      });
      return { customer: c, contracts: filteredVertraege, contractCount, totalBeitrag };
    });
  }, [myCustomers, selectedSparte]);

  const filtered = useMemo(() => {
    return withStats.filter(item => {
      const { customer, contractCount, totalBeitrag } = item;
      if (nameSearch.trim()) {
        const needle = nameSearch.toLowerCase();
        const fullName = `${customer.vorname || ''} ${customer.name || ''}`.toLowerCase();
        const adp = String(customer.adp || '').toLowerCase();
        if (!fullName.includes(needle) && !adp.includes(needle)) return false;
      }
      if (minContracts) {
        const min = parseInt(minContracts, 10);
        if (!isNaN(min) && contractCount < min) return false;
      }
      if (maxContracts) {
        const max = parseInt(maxContracts, 10);
        if (!isNaN(max) && contractCount > max) return false;
      }
      if (minBeitrag) {
        const minB = parseFloat(minBeitrag.replace(',', '.'));
        if (!isNaN(minB) && totalBeitrag < minB) return false;
      }
      if (maxBeitrag) {
        const maxB = parseFloat(maxBeitrag.replace(',', '.'));
        if (!isNaN(maxB) && totalBeitrag > maxB) return false;
      }
      return true;
    });
  }, [withStats, nameSearch, minContracts, maxContracts, minBeitrag, maxBeitrag]);

  const totalContracts = filtered.reduce((sum, item) => sum + item.contractCount, 0);
  const totalBeitraege = filtered.reduce((sum, item) => sum + item.totalBeitrag, 0);

  const resetFilters = () => {
    setSelectedSparte('');
    setMinContracts('');
    setMaxContracts('');
    setMinBeitrag('');
    setMaxBeitrag('');
    setNameSearch('');
  };

  return (
    <PageLayout>
      <PageHeader
        title="Bestandsfilter"
        subtitle="Filtere deinen Bestand nach Sparten, Anzahl der Verträge und Beiträgen."
        actions={
          <div className="flex flex-wrap items-center gap-2">
            <span className={`px-3 py-1.5 rounded-xl font-mono text-xs font-semibold ${isDark ? 'bg-slate-800 text-cyan-300' : 'bg-blue-50 text-blue-700'}`}>Kunden: {filtered.length}</span>
            <span className={`px-3 py-1.5 rounded-xl font-mono text-xs font-semibold ${isDark ? 'bg-slate-800 text-cyan-300' : 'bg-blue-50 text-blue-700'}`}>Verträge: {totalContracts}</span>
            <span className={`px-3 py-1.5 rounded-xl font-mono text-xs font-semibold ${isDark ? 'bg-slate-800 text-cyan-300' : 'bg-blue-50 text-blue-700'}`}>Summe Beiträge: {formatEuro(totalBeitraege)}</span>
          </div>
        }
      />

      <PageCard className="p-4 md:p-5 mb-6">
        <div className="flex items-center gap-2 mb-4">
          <SlidersHorizontal size={18} className={isDark ? 'text-slate-300' : 'text-slate-600'} />
          <span className={`text-sm font-semibold uppercase tracking-wide ${isDark ? 'text-slate-300' : 'text-slate-700'}`}>Filter</span>
        </div>
        <div className="grid gap-4 md:grid-cols-3">
          {/* Name / ADP */}
          <div className="space-y-1">
            <label className={`text-xs font-semibold uppercase tracking-wide ${isDark ? 'text-slate-400' : 'text-slate-600'}`}>Kunde / ADP</label>
            <input
              type="text"
              value={nameSearch}
              onChange={e => setNameSearch(e.target.value)}
              placeholder="Name oder ADP suchen..."
              className={`w-full rounded-xl px-3 py-2 text-sm border outline-none ${isDark ? 'bg-slate-900 border-slate-600 text-white placeholder:text-slate-500' : 'bg-white border-slate-300 text-slate-900 placeholder:text-slate-400'} focus:ring-2 focus:ring-cyan-500`}
            />
          </div>

          {/* Vertragsanzahl */}
          <div className="space-y-1">
            <label className={`text-xs font-semibold uppercase tracking-wide ${isDark ? 'text-slate-400' : 'text-slate-600'}`}>Vertragsanzahl (pro Kunde)</label>
            <div className="flex gap-2">
              <input
                type="number"
                min={0}
                value={minContracts}
                onChange={e => setMinContracts(e.target.value)}
                placeholder="Min."
                className={`w-full rounded-xl px-3 py-2 text-sm border outline-none ${isDark ? 'bg-slate-900 border-slate-600 text-white placeholder:text-slate-500' : 'bg-white border-slate-300 text-slate-900 placeholder:text-slate-400'} focus:ring-2 focus:ring-cyan-500`}
              />
              <input
                type="number"
                min={0}
                value={maxContracts}
                onChange={e => setMaxContracts(e.target.value)}
                placeholder="Max."
                className={`w-full rounded-xl px-3 py-2 text-sm border outline-none ${isDark ? 'bg-slate-900 border-slate-600 text-white placeholder:text-slate-500' : 'bg-white border-slate-300 text-slate-900 placeholder:text-slate-400'} focus:ring-2 focus:ring-cyan-500`}
              />
            </div>
          </div>

          {/* Beiträge */}
          <div className="space-y-1">
            <label className={`text-xs font-semibold uppercase tracking-wide ${isDark ? 'text-slate-400' : 'text-slate-600'}`}>Beitragssumme (WS)</label>
            <div className="flex gap-2">
              <input
                type="text"
                value={minBeitrag}
                onChange={e => setMinBeitrag(e.target.value)}
                placeholder="Min."
                className={`w-full rounded-xl px-3 py-2 text-sm border outline-none ${isDark ? 'bg-slate-900 border-slate-600 text-white placeholder:text-slate-500' : 'bg-white border-slate-300 text-slate-900 placeholder:text-slate-400'} focus:ring-2 focus:ring-cyan-500`}
              />
              <input
                type="text"
                value={maxBeitrag}
                onChange={e => setMaxBeitrag(e.target.value)}
                placeholder="Max."
                className={`w-full rounded-xl px-3 py-2 text-sm border outline-none ${isDark ? 'bg-slate-900 border-slate-600 text-white placeholder:text-slate-500' : 'bg-white border-slate-300 text-slate-900 placeholder:text-slate-400'} focus:ring-2 focus:ring-cyan-500`}
              />
            </div>
          </div>
        </div>

        {/* Sparten-Filter */}
        <div className="mt-4">
          <div className="flex items-center justify-between mb-2">
            <span className={`text-xs font-semibold uppercase tracking-wide ${isDark ? 'text-slate-400' : 'text-slate-600'}`}>Sparten</span>
            <button
              type="button"
              onClick={resetFilters}
              className={`text-xs font-semibold underline-offset-2 hover:underline ${isDark ? 'text-slate-300' : 'text-slate-600'}`}
            >
              Filter zurücksetzen
            </button>
          </div>
          {spartenList.length === 0 ? (
            <p className={`text-xs ${isDark ? 'text-slate-500' : 'text-slate-500'}`}>Keine Sparten gefunden.</p>
          ) : (
            <select
              value={selectedSparte}
              onChange={e => setSelectedSparte(e.target.value)}
              className={`mt-1 w-full md:w-64 rounded-xl px-3 py-2 text-sm border outline-none ${isDark ? 'bg-slate-900 border-slate-600 text-white' : 'bg-white border-slate-300 text-slate-900'} focus:ring-2 focus:ring-cyan-500`}
            >
              <option value="">Alle Sparten</option>
              {spartenList.map(s => (
                <option key={s} value={s}>
                  {s || 'ohne Sparte'}
                </option>
              ))}
            </select>
          )}
        </div>
      </PageCard>

      <PageCard className="overflow-hidden">
        <div className={`hidden md:grid text-xs font-semibold uppercase tracking-wide px-4 py-3 border-b ${isDark ? 'border-slate-700 bg-slate-900/80 text-slate-300' : 'border-slate-200 bg-slate-50 text-slate-600'}`} style={{ gridTemplateColumns: '2fr 90px 120px 120px' }}>
          <span>Kunde</span>
          <span className="text-right">Verträge</span>
          <span className="text-right">Beiträge</span>
          <span className="text-right">Ø Beitrag</span>
        </div>
        {filtered.length === 0 ? (
          <div className={`px-4 py-6 text-sm ${isDark ? 'text-slate-400' : 'text-slate-600'}`}>
            Keine Kunden für die aktuellen Filter gefunden.
          </div>
        ) : (
          <div className="divide-y divide-slate-100 dark:divide-slate-800">
            {filtered.map(({ customer, contracts, contractCount, totalBeitrag }) => {
              const avg = contractCount > 0 ? totalBeitrag / contractCount : 0;
              const name = `${customer.vorname || ''} ${customer.name || ''}`.trim() || (customer.adp || 'Unbekannt');
              const sparten = Array.from(new Set(contracts.map((v: any) => v.sparte).filter(Boolean))).join(', ');
              return (
                <div
                  key={customer.id || `${customer.vorname}-${customer.name}-${customer.adp}`}
                  className={`px-4 py-3 md:grid md:items-center md:gap-3 cursor-pointer ${isDark ? 'hover:bg-slate-800/80' : 'hover:bg-slate-50'}`}
                  style={{ gridTemplateColumns: '2fr 90px 120px 120px' }}
                  onClick={() => onSelectCustomer && onSelectCustomer(customer)}
                >
                  <div>
                    <div className={`text-sm font-semibold ${isDark ? 'text-white' : 'text-slate-900'}`}>{name}</div>
                    <div className={`text-xs ${isDark ? 'text-slate-400' : 'text-slate-600'}`}>
                      ADP: {customer.adp || '–'}{sparten ? ` · Sparten: ${sparten}` : ''}
                    </div>
                  </div>
                  <div className={`mt-2 md:mt-0 text-sm text-right tabular-nums ${isDark ? 'text-slate-200' : 'text-slate-800'}`}>{contractCount}</div>
                  <div className={`mt-1 md:mt-0 text-sm text-right tabular-nums ${isDark ? 'text-slate-200' : 'text-slate-800'}`}>{formatEuro(totalBeitrag)}</div>
                  <div className={`mt-1 md:mt-0 text-sm text-right tabular-nums ${isDark ? 'text-slate-200' : 'text-slate-800'}`}>{contractCount > 0 ? formatEuro(avg) : '–'}</div>
                </div>
              );
            })}
          </div>
        )}
      </PageCard>
    </PageLayout>
  );
};

export default KundenKartei;